import { Component, Input } from '@angular/core';
import { signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { FormSectionField } from '../form-field';
import { BaseFormControl } from '../base-control';
import { collectFieldNames } from './section-fields';

@Component({
    standalone: false,
    selector: 'abs-form-section-base',
    template: ''
})
export class BaseFormSectionComponent extends BaseFormControl {
    @Input()
    field: FormSectionField;

    /**
     * The user's explicit choice, or null when they have not touched the toggle.
     * Null means the auto-open rule decides; once the organiser has opened or closed
     * the section themselves, their choice wins — otherwise a section that opened to
     * reveal an invalid field could never be closed again.
     */
    private userChoice = signal<boolean | null>(null);

    /**
     * Latches the auto-open triggered by hidesBlockedSubmit(). Without this, the
     * section closes the instant the offending control becomes valid — e.g. the
     * organiser is still typing a capacity value and the section snaps shut under
     * them, hiding the other fields they were about to fill in. A plain field
     * (not a signal) because it is set from inside isOpen(), which runs during
     * change detection — writing a signal there risks "expression changed after
     * checked".
     */
    private autoOpened = false;

    isOpen(): boolean {
        if (!this.field?.collapsed) {
            return true;
        }
        const choice = this.userChoice();
        if (choice !== null) {
            return choice;
        }
        if (!this.autoOpened && this.hidesBlockedSubmit()) {
            this.autoOpened = true;
        }
        return this.autoOpened;
    }

    toggle(): void {
        // Toggle against what is actually on screen, so the first click always does
        // something visible regardless of why the section was open.
        this.userChoice.set(!this.isOpen());
    }

    /**
     * True when this section hides a control that is both invalid and touched.
     *
     * A blocked submit marks every control touched (form.component.ts's onSubmit),
     * so this means "the form refused to save and the reason is inside here". Before
     * any submit attempt a required-but-empty control is invalid but untouched, so a
     * section does not spring open merely for being incomplete on load.
     */
    private hidesBlockedSubmit(): boolean {
        const root = this.parentFormGroup?.root as UntypedFormGroup;
        if (!root?.controls) {
            return false;
        }
        return collectFieldNames(this.field?.fields).some((name) => {
            // Literal key access: these names contain dots and are NOT paths.
            const control = root.controls[name];
            return !!control && control.invalid && control.touched;
        });
    }
}
